import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../lib/api'
import type { Company, User, Question, QuestionCategory, UserNote, UserAnswer, Recording } from '../lib/types'
import { CATEGORY_LABELS, CATEGORY_BADGE_CLASS, type CategorySlug } from '../lib/types'
import NoteTaker from '../components/NoteTaker'
import AudioRecorder from '../components/AudioRecorder'
import QuestionList from '../components/QuestionList'
import CompanyAnalysis from '../components/CompanyAnalysis'

interface Props { user: User | null }

export default function CompanyPage({ user }: Props) {
  const { id } = useParams<{ id: string }>()
  const [company, setCompany] = useState<Company | null>(null)
  const [categories, setCategories] = useState<QuestionCategory[]>([])
  const [questions, setQuestions] = useState<Question[]>([])
  const [note, setNote] = useState<UserNote | null>(null)
  const [answers, setAnswers] = useState<UserAnswer[]>([])
  const [recordings, setRecordings] = useState<Recording[]>([])
  const [activeTab, setActiveTab] = useState<'questions' | 'notes' | 'recordings' | 'analysis'>('questions')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    Promise.all([
      api.getCompany(id),
      api.listQuestionCategories(),
      api.listQuestions(),
    ]).then(([c, cats, qs]) => {
      setCompany(c)
      setCategories(cats)
      setQuestions(qs)
    }).finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    if (!id || !user) return
    Promise.all([
      api.getNote(id),
      api.getAnswers(id),
      api.listRecordings(id),
    ]).then(([n, a, r]) => {
      setNote(n)
      setAnswers(a)
      setRecordings(r)
    })
  }, [id, user])

  const handleSaveNote = useCallback(async (content: string) => {
    if (!id) return
    const saved = await api.saveNote(id, content)
    setNote(saved)
  }, [id])

  const handleSaveAnswer = useCallback(async (questionId: string, answer: string, rating?: number) => {
    if (!id) return
    const saved = await api.saveAnswer(id, questionId, answer, rating)
    setAnswers(prev => {
      const idx = prev.findIndex(a => a.question_id === questionId)
      return idx >= 0 ? prev.map((a, i) => i === idx ? saved : a) : [...prev, saved]
    })
  }, [id])

  const handleRecordingSaved = useCallback((recording: Recording) => {
    setRecordings(prev => [recording, ...prev])
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    )
  }

  if (!company) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500 text-lg">Company not found</p>
        <Link to="/companies" className="text-brand-600 mt-2 inline-block hover:underline">Back to exhibitors</Link>
      </div>
    )
  }

  const tabs = [
    { key: 'questions' as const, label: 'Questions', count: questions.length },
    { key: 'notes' as const, label: 'Notes', count: note ? 1 : 0 },
    { key: 'recordings' as const, label: 'Recordings', count: recordings.length },
    { key: 'analysis' as const, label: 'AI Intel', count: 0, icon: '🔍' },
  ]

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <Link to="/companies" className="text-sm text-gray-500 hover:text-brand-600 mb-4 inline-block">
        &larr; Back to exhibitors
      </Link>

      <div className="card mb-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h1 className="text-2xl font-bold">{company.name}</h1>
            {company.booth && (
              <span className="text-sm text-gray-500 font-mono">Booth {company.booth}</span>
            )}
          </div>
          {company.website && (
            <a href={company.website} target="_blank" rel="noopener noreferrer"
              className="text-sm text-brand-600 hover:underline whitespace-nowrap">
              Visit Website &rarr;
            </a>
          )}
        </div>

        {company.description && <p className="text-gray-700 mb-3">{company.description}</p>}

        {company.products && (
          <div className="mb-2">
            <span className="text-sm font-medium text-gray-500">Products: </span>
            <span className="text-sm text-gray-700">{company.products}</span>
          </div>
        )}
        {company.services && (
          <div className="mb-3">
            <span className="text-sm font-medium text-gray-500">Services: </span>
            <span className="text-sm text-gray-700">{company.services}</span>
          </div>
        )}

        <div className="flex gap-1 flex-wrap">
          {company.categories.map(cat => (
            <Link
              key={cat}
              to={`/companies?category=${cat}`}
              className={`badge ${CATEGORY_BADGE_CLASS[cat as CategorySlug] || 'badge-other'} hover:opacity-80`}
            >
              {CATEGORY_LABELS[cat as CategorySlug] || cat}
            </Link>
          ))}
        </div>
      </div>

      {/* Inline Quick Facts from analysis — visible to everyone */}
      {company.analysis?.status === 'complete' && (
        <div className="card bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200 mb-6">
          <h3 className="font-semibold text-indigo-800 flex items-center gap-2 mb-4">
            Quick Facts
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {company.analysis.founded_year && (
              <div className="bg-white rounded-lg p-3 border border-indigo-100">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Founded</div>
                <div className="text-sm font-medium text-gray-800 mt-0.5">{company.analysis.founded_year}</div>
              </div>
            )}
            {company.analysis.headquarters && (
              <div className="bg-white rounded-lg p-3 border border-indigo-100">
                <div className="text-xs text-gray-500 uppercase tracking-wide">HQ</div>
                <div className="text-sm font-medium text-gray-800 mt-0.5">{company.analysis.headquarters}</div>
              </div>
            )}
            {company.analysis.employee_count && (
              <div className="bg-white rounded-lg p-3 border border-indigo-100">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Employees</div>
                <div className="text-sm font-medium text-gray-800 mt-0.5">{company.analysis.employee_count}</div>
              </div>
            )}
            <div className="bg-white rounded-lg p-3 border border-indigo-100">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Status</div>
              <div className="text-sm font-medium text-gray-800 mt-0.5">
                {company.analysis.is_public ? `Public${company.analysis.ticker_symbol ? ` (${company.analysis.ticker_symbol})` : ''}` : 'Private'}
              </div>
            </div>
            {company.analysis.revenue_estimate && (
              <div className="bg-white rounded-lg p-3 border border-indigo-100">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Revenue</div>
                <div className="text-sm font-medium text-gray-800 mt-0.5">{company.analysis.revenue_estimate}</div>
              </div>
            )}
            {company.analysis.industry_focus && (
              <div className="bg-white rounded-lg p-3 border border-indigo-100">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Industry</div>
                <div className="text-sm font-medium text-gray-800 mt-0.5">{company.analysis.industry_focus}</div>
              </div>
            )}
          </div>
          {company.analysis.report && (
            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {company.analysis.report}
            </div>
          )}
        </div>
      )}

      {!user ? (
        <div className="card text-center py-8">
          <p className="text-gray-600 mb-3">Sign in to take notes, answer questions, and record conversations</p>
          <Link to="/login" className="btn-primary inline-block">Sign In</Link>
        </div>
      ) : (
        <>
          <div className="flex border-b mb-4 overflow-x-auto">
            {tabs.map(({ key, label, count, icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === key
                    ? 'border-brand-600 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {icon && <span className="mr-1">{icon}</span>}
                {label}
                {count > 0 && <span className="ml-1 text-xs opacity-60">({count})</span>}
              </button>
            ))}
          </div>

          {activeTab === 'questions' && (
            <QuestionList
              categories={categories}
              questions={questions}
              answers={answers}
              onSaveAnswer={handleSaveAnswer}
            />
          )}

          {activeTab === 'notes' && (
            <NoteTaker
              note={note}
              onSave={handleSaveNote}
              companyName={company?.name}
            />
          )}

          {activeTab === 'recordings' && (
            <AudioRecorder
              companyId={company.id}
              recordings={recordings}
              onRecordingSaved={handleRecordingSaved}
            />
          )}

          {activeTab === 'analysis' && (
            <CompanyAnalysis companyId={company.id} companyName={company.name} />
          )}
        </>
      )}
    </div>
  )
}
